import { ContactDrawer } from "@/components/directory/contacts/ContactDrawer";
import { ContactCreateDialog } from "@/components/directory/contacts/ContactCreateDialog";
import { ContactEditDialog } from "@/components/directory/contacts/ContactEditDialog";
import { OrganizationDrawer } from "@/components/directory/organizations/OrganizationDrawer";
import { OrganizationCreateDialog } from "@/components/directory/organizations/OrganizationCreateDialog";
import { OrganizationEditDialog } from "@/components/directory/organizations/OrganizationEditDialog";
import { PropertyDrawer } from "@/components/directory/properties/PropertyDrawer";
import { PropertyCreateDialog } from "@/components/directory/properties/PropertyCreateDialog";
import { PropertyEditDialog } from "@/components/directory/properties/PropertyEditDialog";
import { LinkedDirectoryRecordDrawer } from "./LinkedDirectoryRecordDrawer";
import type { ClaimDirectory } from "@/hooks/use-claim-directory-links";

interface DirectoryDialogsProps {
    claimId: number;
    dialogs: ClaimDirectory["dialogs"];
    canCreate: boolean;
}

export function DirectoryDialogs({ claimId, dialogs, canCreate }: DirectoryDialogsProps) {
    const { contact, organization, property } = dialogs;
    return (
        <>
            <LinkedDirectoryRecordDrawer
                contact={!contact.detailIsLocal ? contact.detail : null}
                organization={!organization.detailIsLocal ? organization.detail : null}
                property={!property.detailIsLocal ? property.detail : null}
                onClose={() => {
                    contact.setDetail(null);
                    contact.setDetailIsLocal(false);
                    organization.setDetail(null);
                    organization.setDetailIsLocal(false);
                    property.setDetail(null);
                    property.setDetailIsLocal(false);
                }}
            />
            <ContactDrawer
                contact={contact.detailIsLocal ? contact.detail : null}
                open={!!contact.detail && contact.detailIsLocal}
                onClose={() => {
                    contact.setDetail(null);
                    contact.setDetailIsLocal(false);
                }}
                onEdit={(c) => {
                    contact.setDetail(null);
                    contact.setDetailIsLocal(false);
                    contact.setEdit(c);
                    contact.setEditIsLocal(true);
                }}
            />
            <OrganizationDrawer
                organization={organization.detailIsLocal ? organization.detail : null}
                open={!!organization.detail && organization.detailIsLocal}
                onClose={() => {
                    organization.setDetail(null);
                    organization.setDetailIsLocal(false);
                }}
                onEdit={(o) => {
                    organization.setDetail(null);
                    organization.setDetailIsLocal(false);
                    organization.setEdit(o);
                    organization.setEditIsLocal(true);
                }}
            />
            <PropertyDrawer
                property={property.detailIsLocal ? property.detail : null}
                open={!!property.detail && property.detailIsLocal}
                onClose={() => {
                    property.setDetail(null);
                    property.setDetailIsLocal(false);
                }}
                onEdit={(p) => {
                    property.setDetail(null);
                    property.setDetailIsLocal(false);
                    property.setEdit(p);
                    property.setEditIsLocal(true);
                }}
            />

            {/* Create dialogs (auto-linked to this claim) */}
            {canCreate && <ContactCreateDialog
                open={contact.createOpen}
                onClose={() => contact.setCreateOpen(false)}
                createContact={contact.create}
                isCreating={contact.isCreating}
                initialClaimIds={[claimId]}
                hideClaimsPicker
                onCreated={contact.onCreated}
            />}
            {canCreate && <OrganizationCreateDialog
                open={organization.createOpen}
                onClose={() => organization.setCreateOpen(false)}
                createOrganization={organization.create}
                isCreating={organization.isCreating}
                initialClaimIds={[claimId]}
                hideClaimsPicker
                onCreated={organization.onCreated}
            />}
            {canCreate && <PropertyCreateDialog
                open={property.createOpen}
                onClose={() => property.setCreateOpen(false)}
                createProperty={property.create}
                isCreating={property.isCreating}
                initialClaimIds={[claimId]}
                hideClaimsPicker
                onCreated={property.onCreated}
            />}

            {/* Edit dialogs */}
            {contact.editIsLocal && contact.edit && (
                <ContactEditDialog
                    contact={contact.edit}
                    onClose={() => {
                        contact.setEdit(null);
                        contact.setEditIsLocal(false);
                    }}
                    updateContact={contact.update}
                    isUpdating={contact.isUpdating}
                />
            )}
            {organization.editIsLocal && organization.edit && (
                <OrganizationEditDialog
                    organization={organization.edit}
                    onClose={() => {
                        organization.setEdit(null);
                        organization.setEditIsLocal(false);
                    }}
                    updateOrganization={organization.update}
                    isUpdating={organization.isUpdating}
                />
            )}
            {property.editIsLocal && property.edit && (
                <PropertyEditDialog
                    property={property.edit}
                    onClose={() => {
                        property.setEdit(null);
                        property.setEditIsLocal(false);
                    }}
                    updateProperty={property.update}
                    isUpdating={property.isUpdating}
                />
            )}
        </>
    );
}
